import { useState, useEffect, useRef, useCallback } from "react";

// ─── Debug overlay (same as BarcodeScanner) ───────────────────────────────────
// const log = (msg) => {
//   console.log(msg);
//   let d = document.getElementById("dbg");
//   if (!d) {
//     d = document.createElement("div");
//     d.id = "dbg";
//     d.style = "position:fixed;bottom:0;left:0;right:0;background:rgba(0,0,0,0.85);color:lime;font-size:11px;padding:8px;z-index:99999;max-height:40vh;overflow:auto;word-break:break-all;";
//     document.body.appendChild(d);
//   }
//   d.innerHTML += `<div>${new Date().toISOString().slice(11, 19)} — ${msg}</div>`;
//   d.scrollTop = d.scrollHeight;
// };

const log = () => {}

export default function useSpeechInput({ onResult, lang = "de-DE" }) {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);

  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    const hasSpeech = "webkitSpeechRecognition" in window || "SpeechRecognition" in window;
    log("SpeechRecognition supported: " + hasSpeech);

    if (!hasSpeech) {
      log("❌ Speech API not available — mic buttons will be hidden");
      return;
    }

    setSupported(true);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    log("Using: " + (window.SpeechRecognition ? "SpeechRecognition" : "webkitSpeechRecognition"));

    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;
    log("recognition created — lang: " + lang);

    recognition.onstart = () => {
      log("onstart fired ✓");
    };

    recognition.onaudiostart = () => {
      log("onaudiostart — mic is capturing audio ✓");
    };

    recognition.onspeechstart = () => {
      log("onspeechstart — speech detected ✓");
    };

    recognition.onspeechend = () => {
      log("onspeechend — speech stopped");
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.trim();
      const confidence = event.results[0][0].confidence;
      log("onresult ✓ — transcript: \"" + transcript + "\" confidence: " + confidence.toFixed(2));
      if (onResultRef.current) onResultRef.current(transcript);
    };

    recognition.onnomatch = () => {
      log("onnomatch — no speech recognized");
    };

    recognition.onend = () => {
      log("onend fired");
      setListening(false);
    };

    recognition.onerror = (event) => {
      log("❌ onerror — " + event.error + (event.message ? ": " + event.message : ""));
      setListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      log("useSpeechInput cleanup — aborting recognition");
      recognition.abort();
    };
  }, [lang]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !listening) {
      log("startListening called");
      try {
        recognitionRef.current.start();
        setListening(true);
      } catch (e) {
        log("❌ start() threw: " + e.message);
      }
    } else {
      log("startListening skipped — listening:" + listening + " ref:" + !!recognitionRef.current);
    }
  }, [listening]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && listening) {
      log("stopListening called");
      recognitionRef.current.stop();
      setListening(false);
    }
  }, [listening]);

  const toggle = useCallback(() => {
    log("toggle — currently listening: " + listening);
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  }, [listening, startListening, stopListening]);

  return { startListening, stopListening, toggle, listening, supported };
}